import type { CSSProperties, ReactNode } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export function AudienceStatMetricCard({
  label,
  value,
  icon: Icon,
  className,
  style,
}: {
  label: string;
  value: ReactNode;
  icon: LucideIcon;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <Card className={cn('overflow-hidden', className)} style={style}>
      <CardHeader className="space-y-0 bg-secondary px-3 py-2">
        <CardTitle className="flex items-start justify-start gap-1.5 text-xs font-medium text-secondary-foreground ">
          <Icon
            className="size-3.5 mt-[2px] shrink-0"
            data-icon="inline-start"
          />
          <span
            className="overflow-ellipsis whitespace-nowrap overflow-hidden"
            title={label}
          >
            {label}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 py-2.5">
        <div className="text-lg font-semibold leading-tight">{value}</div>
      </CardContent>
    </Card>
  );
}

export function AudienceSizeMetricCard({
  targetUsersCount,
  locale,
}: {
  targetUsersCount: number;
  totalUserbaseCount: number;
  locale: Locales;
}) {
  const { LL } = useI18nContext();

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {LL.audiences.details.analytics.audienceSize()}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <p className="text-xl font-semibold">
              {targetUsersCount.toLocaleString(locale)}
            </p>
            <p className="text-xs text-muted-foreground">
              {LL.audiences.details.overview.usersInAudience()}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
